import React, { useEffect, useRef, useState } from "react";

const SCRIPT_URL =
  "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

export default function TurnstileWidget({
  onVerify,
  resetKey = 0,
  language = "auto",
}) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const callbackRef = useRef(onVerify);
  const [error, setError] = useState("");

  const siteKey = (
    process.env.REACT_APP_TURNSTILE_SITE_KEY || ""
  ).trim();

  useEffect(() => {
    callbackRef.current = onVerify;
  }, [onVerify]);

  useEffect(() => {
    if (!siteKey) {
      setError("Security verification is not configured.");
      callbackRef.current?.("");
      return undefined;
    }

    let cancelled = false;

    const renderWidget = () => {
      if (
        cancelled ||
        !window.turnstile ||
        !containerRef.current ||
        widgetIdRef.current !== null
      ) {
        return;
      }

      try {
        widgetIdRef.current = window.turnstile.render(
          containerRef.current,
          {
            sitekey: siteKey,
            language,
            callback: (token) => {
              setError("");
              callbackRef.current?.(token);
            },
            "expired-callback": () => {
              callbackRef.current?.("");
            },
            "error-callback": () => {
              setError("Security verification could not be completed.");
              callbackRef.current?.("");
            },
          }
        );
      } catch {
        setError("Security verification could not be loaded.");
        callbackRef.current?.("");
      }
    };

    if (window.turnstile) {
      renderWidget();
    } else {
      let script = document.querySelector(
        'script[src*="challenges.cloudflare.com/turnstile"]'
      );

      if (!script) {
        script = document.createElement("script");
        script.src = SCRIPT_URL;
        script.async = true;
        script.defer = true;
        document.head.appendChild(script);
      }

      script.addEventListener("load", renderWidget);
    }

    return () => {
      cancelled = true;

      if (
        window.turnstile &&
        widgetIdRef.current !== null
      ) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch {
          // Widget may already have been removed by Cloudflare.
        }
      }

      widgetIdRef.current = null;
    };
  }, [siteKey, language]);

  useEffect(() => {
    if (
      window.turnstile &&
      widgetIdRef.current !== null
    ) {
      try {
        window.turnstile.reset(widgetIdRef.current);
        callbackRef.current?.("");
        setError("");
      } catch {
        // A subsequent render will recover the widget.
      }
    }
  }, [resetKey]);

  if (!siteKey) {
    return (
      <div
        role="alert"
        className="text-sm text-red-700 bg-red-50 border border-red-200 p-3"
      >
        Security verification is not configured.
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-center w-full overflow-hidden">
        <div
          ref={containerRef}
          data-testid="buyer-turnstile"
          className="w-full max-w-full"
        />
      </div>

      {error && (
        <div
          role="alert"
          className="mt-2 text-sm text-red-700"
        >
          {error}
        </div>
      )}
    </div>
  );
}
